/**
 * Host side of `@astrana/ui/scribe`'s per-section write seam.
 *
 * The library drafts a note from an `EhrEncounterSnapshot` and, on a write,
 * hands back the identity of the chart the draft was DERIVED FROM
 * (`ehrPatientId` + `encounterKey`) — not the chart the host happens to have
 * open when the call lands. Those can differ: a write is an async round trip
 * through the extension bridge into Elation, and a clinician can move to the
 * next patient (or sign the open note and start another) inside it.
 *
 * THIS IS THE PATIENT-SAFETY GATE. The library's own epoch guard discards a
 * stale result, but only after the write has already been dispatched — by then
 * the text is in the chart. Only the host, which owns the transport, can refuse
 * to dispatch. So this comparison FAILS CLOSED and is the last checkpoint
 * before text reaches a chart.
 *
 * Refusal rules (all of them, in order):
 *   1. No chart open at all (no patient id / no open-note id) → refuse.
 *   2. An identifier present in the args differs from the open chart → refuse.
 *   3. An identifier ABSENT from the args while the host HAS one for the open
 *      chart → refuse. An unidentified draft is not a matching draft; treating
 *      "absent" as "fine" is what silently degrades this check to a no-op.
 *
 * Nothing here logs chart text or identifiers — messages are fixed strings.
 */
import { encodeSectionLines } from "@astrana/ui/scribe";
import type {
  NoteSectionLine,
  SectionWriteResult,
  WriteSectionArgs,
  WriteSectionFn,
} from "@astrana/ui/scribe";

import {
  buildSectionWriteRequest,
  type WriteBackMode,
  type WriteBackRequest,
  type WriteBackResult,
} from "@ext/elation/elationWriteBack";
import { writeBackMessage } from "./writeBackMessages";

/**
 * Force each line onto one side of the `NoteSectionLine` union before encoding.
 *
 * NOT a second encoder — nothing here emits the wire format; `encodeSectionLines`
 * from `@astrana/ui/scribe` is the only thing that does. This is the fail-soft
 * net the library's encoder deliberately does not carry: it assumes an
 * already-paired union and emits a half-pair (`systemLabel` with no `finding`, or
 * the reverse) as-is, which the reader inside `stageInPage` — and the library's own
 * `decodeSectionLines` — then reject WHOLESALE, voiding the provenance of every
 * other line in the payload. Demoting the one bad line to prose instead keeps it
 * reaching the chart verbatim in the section's list entry and leaves the rest
 * attributed.
 *
 * The union makes a half-pair unconstructible in typed code, so on the library's
 * own drafter path this is the identity function. It earns its place because
 * `lines` crosses a runtime boundary from a separately versioned package: the
 * pairing is a compile-time guarantee about code we do not compile.
 */
function pairedLines(lines: readonly NoteSectionLine[]): NoteSectionLine[] {
  return lines.map((line) => {
    const label = typeof line.systemLabel === "string" ? line.systemLabel : null;
    const finding = typeof line.finding === "string" ? line.finding : null;
    const text = typeof line.text === "string" ? line.text : "";
    return label !== null && finding !== null
      ? { text, systemLabel: label, finding }
      : { text, systemLabel: null, finding: null };
  });
}

/** The chart the host currently has open, as the shell knows it. */
export interface OpenChartIdentity {
  /** EHR patient id of the chart on screen (`ElationPatient.ehrPatientId`). */
  ehrPatientId?: string;
  /** Id of the OPEN note on screen (`ElationNote.noteId`). */
  noteId?: string;
  /**
   * Whether a note is open at all, independent of whether we could learn its
   * id. These are DIFFERENT questions and conflating them broke every write.
   *
   * `noteId` comes from a DOM scrape whose hidden-input selectors do not match
   * the live Elation page (verified 2026-08-05: `#note-new-visit-form` present,
   * `id`/`noteId`/`visitNoteId` all absent). Treating "no id" as "no note open"
   * made rule 1 below refuse EVERY write with NO_OPEN_CHART_MESSAGE, even with
   * a note plainly open on screen.
   *
   * Fail-closed is preserved where it matters: the patient check is unchanged,
   * and `identityMismatches` already treats an absent host-side id as "nothing
   * to contradict" rather than as a mismatch. This makes rule 1 agree with it.
   */
  hasOpenNote?: boolean;
}

export interface WriteSectionDeps {
  /** Transport. In the app this is `requestElationWrite`. */
  write: (req: WriteBackRequest) => Promise<WriteBackResult>;
  /** Optional post-commit nudge to re-scrape the chart. */
  refresh?: () => void;
  /** Defaults to "stage" — the clinician's own Save/Sign stays the gate. */
  mode?: WriteBackMode;
}

export const NO_OPEN_CHART_MESSAGE =
  "Open a note in Elation before writing.";

export const PATIENT_CHANGED_MESSAGE =
  "The chart changed since this note was drafted. Re-open the patient and try again.";

export const NOTE_CHANGED_MESSAGE =
  "The open note changed since this was drafted. Refresh and try again.";

export const EMPTY_TEXT_MESSAGE = "There's nothing to write.";

/**
 * Compare one drafted-from identifier against the open chart's.
 * Returns true when the write must be REFUSED.
 */
function identityMismatches(
  fromDraft: string | undefined,
  openNow: string | undefined,
): boolean {
  // Host has no such identifier: nothing to contradict, and rule 1 has already
  // established there is a chart open.
  if (!openNow) return false;
  // Rule 3 — absent in the args while the host has one.
  if (!fromDraft) return true;
  // Rule 2 — present and different.
  return fromDraft !== openNow;
}

/**
 * Build the `onWriteSection` seam handed to `<AmbientScribePanel>`.
 *
 * `chart` must be the chart open RIGHT NOW; pass a fresh object on every
 * re-read so the closure never compares against a stale snapshot.
 */
export function createWriteSectionHandler(
  chart: OpenChartIdentity,
  deps: WriteSectionDeps,
): WriteSectionFn {
  const mode: WriteBackMode = deps.mode ?? "stage";

  return async function writeSection(
    args: WriteSectionArgs,
  ): Promise<SectionWriteResult> {
    const patientId = chart.ehrPatientId;
    const noteId = chart.noteId;

    // 1. Nothing open to write into. An id we could not READ is not the same
    // as a note that is not OPEN — see `hasOpenNote`.
    if (!patientId || (!noteId && !chart.hasOpenNote)) {
      return { ok: false, message: NO_OPEN_CHART_MESSAGE };
    }
    // COMMIT posts to `visit-notes/<noteId>/bullet/` — without a real id there
    // is no correct URL to post to, so it must still refuse. STAGE resolves the
    // open note itself in the page world and never uses this id, which is why
    // it can proceed. Only STAGE is reachable from the app today (the default).
    if (!noteId && mode === "commit") {
      return { ok: false, message: NO_OPEN_CHART_MESSAGE };
    }

    // 2/3. FAIL CLOSED — the draft did not come from the chart open now.
    if (identityMismatches(args.ehrPatientId, patientId)) {
      return { ok: false, message: PATIENT_CHANGED_MESSAGE };
    }
    if (identityMismatches(args.encounterKey, noteId)) {
      return { ok: false, message: NOTE_CHANGED_MESSAGE };
    }

    const text = (args.text ?? "").trim();
    if (!text) return { ok: false, message: EMPTY_TEXT_MESSAGE };
    if (!args.sectionKey) {
      return { ok: false, message: writeBackMessage({
        ok: false,
        status: 0,
        readAfterWrite: false,
        errorCode: "SECTION_UNRESOLVED",
      }) };
    }

    // PROVENANCE, not a second copy of the text. When the drafter tells us
    // which system each line names, that attribution travels beside the body so
    // the injected writer never has to re-derive it by re-reading — and
    // rewriting — the physician's words. Absent (older library, or a section
    // with no per-system structure) the writer falls back to parsing `text`.
    //
    // The encoder is the LIBRARY's, so there is exactly one of it; `pairedLines`
    // above only normalises the union it assumes.
    const carried = args.lines;
    const linesJson =
      carried && carried.length > 0
        ? encodeSectionLines(pairedLines(carried))
        : undefined;

    const result = await deps.write(
      buildSectionWriteRequest({
        linesJson,
        patientId,
        // Empty only on the STAGE path, which resolves the note in-page; the
        // guard above refuses COMMIT without a real id.
        noteId: noteId ?? "",
        sectionKey: args.sectionKey,
        text,
        mode,
      }),
    );

    if (result.ok) {
      // COMMIT persists but doesn't live-update the open editor — nudge a
      // re-scrape so the panel's next snapshot reflects the write.
      if (mode === "commit") deps.refresh?.();
      return { ok: true };
    }

    // Raw codes never reach the clinician (writeBackMessages.ts owns the copy).
    return { ok: false, message: writeBackMessage(result) };
  };
}
